import { Injectable } from '@angular/core';
import { from, Observable, of } from 'rxjs';
import { HttpEvent, HttpEventType, HttpProgressEvent, HttpResponse } from '@angular/common/http';
import { Platform } from '@ionic/angular';
import { CurrentSettings, State, StateInterface } from '../../interfaces/biozoom-api/app-configuration';
import { ScannerStatus } from '../../interfaces/scanner-api/scanner-status';
import { catchError, delay, repeat, switchMap, timeout } from 'rxjs/operators';
import * as uuid from 'uuid';
import {evalJsonWrapper, parseHandRecognition} from '../../methods/blockFormat';
import {PipelineStageConfig} from '../../interfaces/biozoom-api/pipeline';
import {getKeyVal} from '../../methods/helpers';

const expectedKeys = [
  'host',
  'product',
  'serial',
  'sensor',
  'sensor_firmware',
  'sensor_temp',
  'ip',
  'battery_voltage',
  'usb_voltage',
  'usb_charging',
  'memory_free_64k_blocks',
  'memory_healthy',
  'wifi_firmware',
  'wifi_name',
  'wifi_strength',
  'uptime',
  'svn',
  'timestamp',
  'production',
  'meas_count_wifi',
  'capabilities',
  'capabilities.SUPPORT_SPI_MEMORY',
  'capabilities.SUPPORT_CHARGE_CONTROL',
  'capabilities.SUPPORT_IDLE_TIMER',
  'capabilities.SUPPORT_MDNS',
  'capabilities.SUPPORT_USB_LEGACY_MODE',
  'capabilities.WITH_CHARGE_LED',
  'capabilities.AP_PASS',
  'capabilities.CORE_DEBUG_LEVEL'
];
const expectedKeysNew = [
  'nuvoton_firmware',
  'nuvoton_chip_id',
  'serial',
  'sensor',
  'battery_voltage',
  'usb_voltage',
  'usb_charging',
];

interface Port {
  readable: ReadableStream;
  writable: WritableStream;
  forget: () => Promise<undefined>;
  getInfo: () => { usbVendorId?: number, usbProductId?: number };
  open:
  (options:
    { baudRate: number, bufferSize?: number, dataBits?: number, flowControl?: string, parity?: string, stopBits?: number }
  ) => Promise<void>;
  setSignals: (options?: { dataTerminalReady?: boolean, requestToSend?: boolean, break?: boolean }) => Promise<void>;
  getSignals: () => Promise<{ clearToSend: boolean, dataCarrierDetect: boolean, dataSetReady: boolean, ringIndicator: boolean }>;
  close: () => Promise<void>;
}

interface Serial {
  requestPort: (params?: { filters: { usbVendorId?: number }[] }) => Promise<Port>;
  getPorts: () => Promise<Port[]>;
}


@Injectable({
  providedIn: 'root'
})
export class WebUsbApiService {
  // private lastReadSerial: string;
  // private serialRegEx = '\\d[A-Z][0-9A-F]{18}';
  private oldSerialRegEx = '[45]W[0-9A-F]{18}';
  private isUSB = false;
  private firstConnection = false;
  private isEnabled: boolean;
  private serial: Serial;
  private port: Port;
  private vid = 0;
  private serialNumber: string;
  private version = 1;
  private isRequesting: boolean;
  private baudRate: number;
  private readBytes = 0;
  private addSleep = false;
  private baudRates: number[] = [2000000, 500000];


  constructor(
    private platform: Platform,
  ) {
    this.platform = platform;
    // @ts-ignore
    if (this.platform.is('windows')) {
      // @ts-ignore
      // this.serial = navigator.serial;
      this.isEnabled = true;
      this.isRequesting = false;
    }
  }

  async awaitSleep(ms = 250) {
    if (!this.addSleep) {
      await new Promise(r => setTimeout(r, ms));
    } else {
      await new Promise(r => setTimeout(r, ms + 500));
    }
  }

  async disableUSB() {
    if (!this.isUSB) { return; }
    await this.executeCommand('X\n');
    this.isUSB = false;
  }

  async enableUSB(firstConnection = false) {
    if (this.version === 2) {
      return;
    }
    if (this.isUSB) { return; }
    if (!((this.serialNumber ?? '').startsWith('4W')) && !((this.serialNumber ?? '').startsWith('5W')) && !firstConnection) {
      await this.executeCommand('X\n');
      // await new Promise((r) => setTimeout(r, 500));
      await this.executeCommand('start_usb\n');
      await new Promise((r) => setTimeout(r, 500));
      await this.read();
    }
    this.isUSB = true;
  }

  async prepareConnection(isUSB = true, forceRestart = false) {
    let restart = !this.addSleep;
    if (!this.port.readable) {
      await this.port.open({ baudRate: this.baudRate });
      await this.awaitSleep();
    }
    if (isUSB && !this.isUSB) {
      if (!((this.serialNumber ?? '').startsWith('4W')) && !((this.serialNumber ?? '').startsWith('5W'))) {
        restart = true;
        await this.executeCommand('X\n');
        await this.executeCommand('start_usb\n');
        this.isUSB = true;
      }
    } else if (!isUSB && this.isUSB) {
      restart = !this.addSleep;
      await this.executeCommand('X\n');
      this.isUSB = false;
    }

    if (forceRestart) {
      await this.read('W');
      // restart = true;
    }

    if (restart) {
      if (this.port.readable) {
        try {
          await this.port.close();
          // await new Promise((r) => setTimeout(r, 1500));
        } catch (e) { console.log(e); }
      }
      await this.port.open({ baudRate: this.baudRate });
      // await new Promise((r) => setTimeout(r, 250));
      await this.awaitSleep();
      // await new Promise((r) => setTimeout(r, 1500));
    }
  }

  /**
   * Performs hand recognition based on the given settings.
   * @param settings - The current settings for hand recognition.
   * @returns An Observable that emits a boolean value indicating if hand recognition was successful.
   */
  async doHandrecognitionObservable(state: StateInterface, settings: CurrentSettings, handOff = false): Promise<boolean> {
    if (this.version === 2) {
      return this.doHandrecognitionObservable2(state, settings, handOff);
    }
    await this.prepareConnection(true);
    const res = await this.executeCommand('W');
    try {
      let result;
      const resJson = JSON.parse(res);
      const dark: number = resJson[0];
      const bright: number = resJson[1];
      if (this.addSleep) {
        await new Promise(r => setTimeout(r, 500));
      }
      if (bright < 0.0001) {
        return false;
      }
      if (state.SCANNER_SERIAL && state.SCANNER_SERIAL.match(this.oldSerialRegEx)) {
        result = (dark < 2) && ((bright > 500) && (bright < 6000));
      } else {
        // if (dark === 0 && bright < 1) {
        //   return this.doHandrecognitionObservable(settings);
        // }
        const darkUpper = settings.handRecognitionLimitDarkUpper;
        const brightLower = settings.handRecognitionLimitBrightLower;
        const brightUpper = settings.handRecognitionLimitBrightUpper;
        result = (dark < darkUpper) && ((bright > brightLower) && (bright < brightUpper));
      }
      if (handOff) {
        result = !result;
      }
      return result;
    } catch (e) { return false; }
  }

  /**
   * Performs an AOX measurement.
   *
   * @param serial The serial number of the scanner. Defaults to undefined.
   * @returns An observable that emits HttpEvents during the measurement process.
   *                                      - The observable completes when the measurement is finished.
   */
  // doAOXMeasurementObservable(): Observable<HttpEvent<any>> {
  //   const totalLength = 254012;
  //
  //   return of({}).pipe(
  //     switchMap(async _ => {
  //       await this.prepareConnection(true);
  //       const res = await this.executeCommand('M');
  //       if (res.length !== totalLength) {
  //         return new HttpResponse({body: ''});
  //       }
  //       return new HttpResponse({body: res});
  //     }),
  //     catchError((err) => {
  //       return of(new HttpResponse({body: ''}));
  //     })
  //   );
  // }

  doAOXMeasurementObservable(measurementType: string): Observable<HttpEvent<any>> {
    // TODO
    const totalLength = 254012;

    return this.readMeasurement(measurementType);
  }

  async getStatus(state: StateInterface): Promise<ScannerStatus> {
    if (this.version === 2) {
      return this.getStatus2(state);
    }
    // TODO: Matrix
    await this.prepareConnection(false);
    const res = await this.executeCommand('get_status\n');
    if (res && res.length > 0) {
      try {
        const jsonString = res.replace('\r\n', '');
        const json = JSON.parse(jsonString);
        const keys = [];
        for (const key of Object.keys(json)) {
          keys.push(key);
        }
        if (keys.includes('capabilities')) {
          for (const key of Object.keys(json.capabilities)) {
            keys.push(`capabilities.${key}`);
          }
        }
        for (const key of ((state.SCANNER_SERIAL ?? '').startsWith('1N') ? expectedKeysNew : expectedKeys)) {
          if (!(keys.includes(key))) {
            return;
          }
        }
        // await serialDispatcher.write('start_usb\n');
        return json;
      } catch (e) {
        return;
      }
    }
    return;
  }

  async write(message: string) {
    try {
      const encoder = new TextEncoder();
      const writer = this.port.writable.getWriter();
      await writer.write(encoder.encode(message));
      writer.releaseLock();
    } catch (e) {
      return false;
    }
    return true;
  }

  bufferToHex(buf) {
    return [...new Uint8Array(buf)]
      .map(b => b.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase();
  }

  readMeasurement(measurementType: string) {
    if (this.version === 2) {
      return this.readMeasurement2(measurementType);
    }
    const timeoutVal = 250;
    let expectedBytes = 190509;

    if (['4W', '5W'].includes(this.serialNumber.slice(0, 2))) {
      expectedBytes = 73845;
    }

    const initFunc = async () => {
      await this.prepareConnection(true, true);
      await this.write('M');
      const reader = this.port.readable.getReader();
      return reader;
    };
    let readValue: Uint8Array = new Uint8Array();

    return from(initFunc()).pipe(
      switchMap(async (reader) => {
        let isDone = await Promise.race<boolean>([
          new Promise(async (resolve) => {
            try {
              const { value, done } = await reader.read();
              readValue = new Uint8Array([...readValue, ...value]);
              resolve(done);
            } catch (e) {
              //
            }
          }),
          new Promise((resolve, reject) => setTimeout(() => {
            resolve(true);
          }, timeoutVal))
        ]);
        if (readValue.length >= expectedBytes) {
          isDone = true;
        }
        if (isDone) {
          const buffer = readValue.buffer;
          const r = Buffer.from(buffer).toString('base64');
          reader.releaseLock();
          await new Promise(r => setTimeout(r, 2000));
          return new HttpResponse({ body: r });
        }
        return { type: HttpEventType.DownloadProgress, loaded: (readValue.length), total: expectedBytes } as HttpProgressEvent;
      }),
      // switchMap((r) => {
      //   if (r.type === 4) {
      //     return [true, r];
      //   } else {
      //     return [false, r];
      //   }
      // }),
      delay(2),
      // timeout(this.addSleep ? 2500 : 1000),
      // timeout(2500 + (this.addSleep ? 1500 : 500)),
      timeout(2500),
      repeat(),
      catchError((err) => {
        console.log(err);
        return of(new HttpResponse({ body: '' }));
      })
    );
  }

  async read(command: string = '') {
    this.readBytes = 0;
    let timeoutVal = 500;
    let expectedBytes = 1000;


    switch (command) {
      case 'M': timeoutVal = 250; expectedBytes = 190509; break;
      case 'W': timeoutVal = 1000; expectedBytes = 44; break;
      case 'I': timeoutVal = 200; expectedBytes = 28; break;
      case 'start_usb\n':
      case 'X\n': timeoutVal = 10; expectedBytes = 0; break;
      default: timeoutVal = 100; break;
    }
    let readValue: Uint8Array = new Uint8Array();
    let isDone = false;

    // while (this.port.readable) {
    const reader = this.port.readable.getReader();
    try {
      while (true) {
        isDone = await Promise.race<boolean>([
          new Promise(async (resolve) => {
            try {
              const { value, done } = await reader.read();
              readValue = new Uint8Array([...readValue, ...value]);
              resolve(done);
            } catch (e) {
              //
            }
          }),
          new Promise((resolve, reject) => setTimeout(() => {
            resolve(true);
          }, timeoutVal))
        ]);
        this.readBytes = readValue.length;
        if (isDone || (readValue.length >= expectedBytes)) {
          // |reader| has been canceled.
          break;
        }
        // Do something with |value|…
      }
    } catch (error) {
      // Handle |error|…
    } finally {
      reader.releaseLock();
    }

    // if (isDone) {
    //   break;
    // }
    return readValue;
  }

  async executeCommand(command: string) {
    await this.write(command);
    // if (['start_usb\n', 'X\n'].includes(command)) {
    //   await new Promise((r) => setTimeout(r, 500));
    //   return '';
    // }
    // await new Promise((r) => setTimeout(r, 500));
    // if (this.addSleep) {
    //   await new Promise((r) => setTimeout(r, 250));
    // }
    const messageArray = await this.read(command);
    if (messageArray.length === 0) {
      return '';
    }
    let result: string;
    switch (command) {
      case 'I': {
        if (messageArray.length < 28) {
          return '';
        }
        result = (new TextDecoder('utf-8')).decode(messageArray.slice(2, 2 + 6));
        result += this.bufferToHex(messageArray.slice(2 + 6, 2 + 13));
        break;
      }
      case 'S':
      case 'get_status\n': {
        const buffer = messageArray.buffer;
        const r = await new Response(buffer).json();
        result = JSON.stringify(r);
        break;
      }
      case 'W': {
        const dv = new DataView(messageArray.buffer);
        const dark = dv.getFloat32(2, true);
        const bright = dv.getFloat32(2 + 2 * 4, true);
        result = JSON.stringify([dark, bright]);
        break;
      }
      case 'M': {
        const buffer = messageArray.buffer;
        const r = Buffer.from(buffer).toString('base64');
        result = r;
        break;
      }
      default: {
        break;
      }
    }
    return result;
  }

  async openPort(setState: ((key: State, val: any) => void)) {
    if (!this.vid) {
      return;
    }
    if (this.port.readable !== null && this.serialNumber !== undefined) {
      await this.enableUSB();
      return this.serialNumber;
    }

    if (this.port.readable) {
      try {
        await this.port.close();
      } catch (e) { console.log(e); return this.serialNumber; }
    }
    // await new Promise((r) => setTimeout(r, 1500));

    this.serialNumber = undefined;
    this.version = 1;
    for (const baudRate of this.baudRates) {
      try {
        await this.port.open({ baudRate });
        const isWindows = navigator.userAgent.includes('Windows');
        if (isWindows) {
          await this.awaitSleep(10);
          await this.port.setSignals({ dataTerminalReady: true, requestToSend: true });
          await this.awaitSleep(10);
          await this.port.setSignals({ dataTerminalReady: true, requestToSend: false });
          await this.awaitSleep(10);
          await this.port.setSignals({ dataTerminalReady: false, requestToSend: false });
        } else {
          await this.awaitSleep();
          await this.port.setSignals({ dataTerminalReady: false });
          await this.awaitSleep(10);
          await this.port.setSignals({ requestToSend: false });

        }
        await this.awaitSleep(2000);
      } catch (e) {
        console.log(e);
        continue;
      }
      this.isUSB = false;

      try {
        await this.enableUSB();
        await this.read();
        const message = await this.executeCommand('I');

        if (!message || message === '' ||
          !(['4W', '5W', '1E', '1N', '2E', '2N'].includes(message.slice(0, 2)))
        ) {
          await this.executeCommand('\n');
        } else {
          const ind = this.baudRates.indexOf(baudRate);
          this.baudRates.splice(ind, 1);
          this.baudRates.unshift(baudRate);
          this.baudRate = baudRate;
          this.serialNumber = message;
          if (['1N', '2N'].includes(message.slice(0, 2))) {
            const status = await this.executeCommand('S');
            if (status !== '') {
              try {
                const statusJSON = JSON.parse(status);
                setState(State.SCANNER_STATUS, statusJSON);
                setState(State.CONNECTION_TYPE, 'USB');
                setState(State.USB_MODE, true);
              } catch (e) {
                //
              }
            }
          }
          break;
        }
      } catch (e) { }
      let rev2Status = await this.executeCommand2('get_status\n', true);
      if (rev2Status === '') {
        rev2Status = await this.executeCommand2('status\n', true);
      } else if (rev2Status.includes('error')) {
        rev2Status = await this.executeCommand2('status\n', true);
      }
      // const rev2Status = '';
      if (rev2Status !== '') {
        try {
          const rev2JSON = JSON.parse(rev2Status);
          setState(State.SCANNER_STATUS, rev2JSON);
          setState(State.CONNECTION_TYPE, 'USB');
          setState(State.USB_MODE, true);
          if (rev2JSON.command) {
            this.version = 2;
          }
          const ind = this.baudRates.indexOf(baudRate);
          this.baudRates.splice(ind, 1);
          this.baudRates.unshift(baudRate);
          this.baudRate = baudRate;
          this.serialNumber = rev2JSON.serial;
          break;
        }
        catch (e) { }
      } else {
      }
      if (this.port.readable) {
        try {
          await this.port.close();
        } catch (e) { }
      }
    }
    return this.serialNumber;
  }

  async requestPermission(
    state: StateInterface,
    setState: ((key: State, val: any) => void),
    setBlockScreen: ((block: boolean) => void) = (() => { })
  ): Promise<[string, number]> {
    if (!this.platform.is('desktop')) {
    }

    // @ts-ignore
    this.serial = navigator.serial;
    if (!this.serial) {
      return ['', 0];
    }

    setBlockScreen(true);
    const VIDs = [0x04B4, 0x10C4, 0x0416, 0xEA60, 0x04b2, 0xB002];
    this.vid = undefined;
    this.serialNumber = undefined;

    this.isEnabled = true;
    const ports = await this.serial.getPorts();
    if (ports.length > 0) {
      const status = ports[0].getInfo();
      this.vid = status.usbVendorId;
      if (this.vid) {
        this.addSleep = ((status.usbVendorId === 0x10C4 || status.usbVendorId === 0x0416) && navigator.userAgent.includes('Windows'));
        this.port = ports[0];
      }
    } else {
      this.firstConnection = false;
      this.isRequesting = true;
      this.serial
        .requestPort({ filters: VIDs.map((vid) => ({ usbVendorId: vid })) })
        .then(async (p) => {
          const status = p.getInfo();
          this.port = p;
          this.vid = status.usbVendorId;
          this.isRequesting = false;
          this.addSleep = ((status.usbVendorId === 0x10C4 || status.usbVendorId === 0x0416) && navigator.userAgent.includes('Windows'));
        })
        .catch(async (e) => {
          this.isRequesting = false;
        });
    }
    while (this.isRequesting) {
      await new Promise(r => setTimeout(r, 10));
    }
    if (!this.vid) {
      setBlockScreen(false);
      return ['', 0];
    }
    await this.openPort(setState);
    this.firstConnection = true;
    setBlockScreen(false);
    // await this.enableUSB();
    return [this.serialNumber, this.version];
    // await this.handleTest();
  }





  async prepareConnection2(isUSB = true, forceRestart = false) {
    if (!this.port.readable) {
      await this.port.open({ baudRate: this.baudRate });
      await this.awaitSleep();
      await this.port.setSignals({ dataTerminalReady: false });
      await this.awaitSleep(10);
      await this.port.setSignals({ requestToSend: false });
      await this.awaitSleep(10);
      // await this.read();
      await this.awaitSleep(2000);
    }
  }

  async read2(command: string = '', id: string = '') {
    let readValue: Uint8Array = new Uint8Array();
    let isDone = false;
    let timeoutDur = 1000;
    if (command.startsWith('read-flash')) {
      timeoutDur = 5000;
    } else if (command.startsWith('get_status')) {
      timeoutDur = 2000;
    } else if (command.startsWith('delete')) {
      timeoutDur = 10000;
    }
    let result = '';

    // while (this.port.readable) {
    let reader: ReadableStreamDefaultReader;
    try {
      reader = this.port.readable.getReader();
      while (true) {
        isDone = await Promise.race<boolean>([
          new Promise(async (resolve) => {
            try {
              const { value, done } = await reader.read();
              readValue = new Uint8Array([...readValue, ...value]);
              resolve(false);
            } catch (e) {
              //
            }
          }),
          new Promise((resolve, reject) => setTimeout(() => {
            resolve(true);
          }, timeoutDur))
        ]);
        while (readValue.length > 0 && readValue[0] === 128) {
          readValue = readValue.slice(1);
        }
        this.readBytes = readValue.length;
        const buffer = readValue.buffer;
        const r1 = await new Response(buffer).text();
        if (isDone) {
          break;
        }
        if (!r1.includes('\n')) {
          continue;
        }
        const dataSplit = r1.split('\n');

        if (command.startsWith('read-flash')) {
          result = r1;
          break;
        }
        let isDone2 = false;
        for (const data of dataSplit) {
          try {
            const r = JSON.parse(data);
            if (r.command === undefined) {
              continue;
            }
            if (r?.['call-index'] !== undefined && r?.['call-index'] && id !== '') {
              if (r?.['call-index'] !== id) {
                continue;
              }
            }
            isDone2 = true;
            result = data;
            break;
          } catch (e) {

          }
        }
        if (isDone2) {
          break;
        }
      }
    } catch (error) {
    } finally {
      try {
        reader.releaseLock();
      } catch (e) {}
    }
    return result;
  }

  async write2(message: string, init = false) {
    const id = uuid.v4().slice(0, 8);
    let commandAdjusted = message;
    if (!init) {
      if (commandAdjusted.includes('?')) {
        commandAdjusted += '&';
      } else {
        commandAdjusted += '?';
      }
      commandAdjusted += `call-index=${id}\n`;
    }
    try {
      const encoder = new TextEncoder();
      const writer = this.port.writable.getWriter();
      await writer.write(encoder.encode(commandAdjusted));
      writer.releaseLock();
    } catch (e) {
      return ['', id];
    }
    return [commandAdjusted, id];
  }

  async executeCommand2(command: string, init = false) {

    const [commandAdjusted, id] = await this.write2(command, init);
    const messageString = await this.read2(commandAdjusted, id);
    return messageString;
  }

  async doHandrecognitionObservable2(state: StateInterface, settings: CurrentSettings, handOff = false): Promise<boolean> {
    await this.prepareConnection2(true);

    const firmware: string = state?.SCANNER_STATUS?.wifi_firmware ?? '';
    const firmwareNuv: string = state?.SCANNER_STATUS?.nuvoton_firmware ?? '';
    const vSplit = firmware.split('.');
    const vSplitNuv = firmwareNuv.split('.');
    if (vSplit.length > 2 || vSplitNuv.length > 2) {
      const v0 = Number(vSplit[0]);
      const v1 = Number(vSplit[1]);
      const v2 = Number(vSplit[2]);
      const vNuv1 = Number(vSplitNuv[1]);
      const newProtocol = (v0 !== 10 && v0 !== 20 && v0 > 0) || v1 > 0 || v2 > 19 || vNuv1 >= 4;
      if ( newProtocol ) {
        const start = handOff ? 134 : 110;
        const count = handOff ? 10 : 13;
        const cycles = handOff ? 25 : 5;

        const resInner = await this.executeCommand2(`hand?start=${start}&count=${count}&cycles=${cycles}`);
        try {
          const jsonParsed = JSON.parse(resInner);
          const result = parseHandRecognition(jsonParsed.data, handOff, start, count, cycles);
          if (result) {
            const dataToSend = [];
            for (let i = 0; i < jsonParsed.data.length; i++) {
              if (((i + 1) % (cycles + 2)) === 0) {
                continue;
              }
              dataToSend.push(jsonParsed.data[i]);
            }
            state.HAND_RECOGNITION_DATA = dataToSend;
          }
          return result;
        } catch (e) {
          return false;
        }
      }
    }

    const darkUpper = settings.handRecognitionLimitDarkUpper;
    const brightLower = settings.handRecognitionLimitBrightLower;
    const brightUpper = settings.handRecognitionLimitBrightUpper;

    const res = await this.executeCommand2(`hand?du=${darkUpper}&bl=${brightLower}&bu=${brightUpper}`);

    try {
      let result = JSON.parse(res).hand === 0;
      if (handOff) {
        result = !result;
      }
      return result;
    } catch (e) {
      return false;
    }
  }

  async getStatus2(state: StateInterface): Promise<ScannerStatus> {
    await this.prepareConnection2(false);

    let res;
    if (state.SCANNER_SERIAL.startsWith('2N')) {
      res = await this.executeCommand2('status');
    } else {
      res = await this.executeCommand2('get_status');
    }
    try {
      const json = JSON.parse(res);
      return json;
    } catch (e) {
      return;
    }
    return;
  }

  readMeasurement2(measurementType: string) {

    let totalBlocks = 5773;
    let command = 'measure?type=aox-prod';
    let resultIndex = 0;
    switch (measurementType) {
      case 'full': {
        totalBlocks = 5773;
        command = 'measure?type=aox';
        break;
      }
      case 'front_half': {
        totalBlocks = 2887;
        command = 'measure?type=aox-half';
        break;
      }
      default: {
        totalBlocks = 5773;
        command = 'measure?type=aox';
        break;
      }
    }
    const foundIndexes = Array(totalBlocks).fill(false);
    const resDict = {};
    let missingArray = [[0, 150]];
    let goodBlocks = 0;
    let startTS: number;
    let command2: string;
    let id2: string;
    const initFunc = async () => {
      if (resultIndex > 0) {
        return;
      }
      await new Promise((r) => setTimeout(r, 1000));
      const [command1, id1] = await this.write2('delete');
      await this.read2(command1, id1);
      [command2, id2] = await this.write2(command);
      await new Promise((r => setTimeout(r, 250)));
      return;
    };

    return from(initFunc()).pipe(
      switchMap(async _ => {
        if (resultIndex > 0) {
          return resultIndex;
        }
        const resultRaw = await this.read2(command2, id2);
        startTS = Date.now();
        try {
          const resultJson = JSON.parse(resultRaw);
          resultIndex = resultJson.measurement_nr;
          return resultIndex;
        } catch (e) {
          return 0;
        }
        // return 60;
      }),
      delay(100),
      switchMap(async _ => {

        if (goodBlocks === totalBlocks) {
          return new HttpResponse({ body: missingArray.join('') });
        }

        const currentTS = Date.now();
        const blocksPerSecond = 400;
        const currentEntry = missingArray[0];
        const currentMax = Math.floor(((currentTS - startTS) / 1000) * blocksPerSecond);

        const start = currentEntry[0];
        let range = currentEntry[1];

        if (currentMax <= start) {
          return { type: HttpEventType.DownloadProgress, loaded: (goodBlocks), total: totalBlocks } as HttpProgressEvent;
        }



        if (start + range > currentMax) {
          range = currentMax - start + 1;
        }
        const r = await this.executeCommand2(`read-flash?nr=${resultIndex}&block=${start}&range=${range}`);
        if (r !== '') {
          const { status: statusEval, data: d, goodBlocks: gb, ok } = evalJsonWrapper(r, resDict, totalBlocks, foundIndexes);
          if (ok) {
            goodBlocks = gb;
            missingArray = d;
          }
          if (statusEval) {
            return new HttpResponse({ body: missingArray.join('') });
          }
        }
        return { type: HttpEventType.DownloadProgress, loaded: (goodBlocks), total: totalBlocks } as HttpProgressEvent;
      }),
      delay(100),
      repeat(),
    );
  }

  async finalizeScanner(pipelineConfig: PipelineStageConfig) {
    const productGroup = pipelineConfig.state?.[pipelineConfig?.stageConfig?.resGroup] ?? {};
    const product = getKeyVal(pipelineConfig?.stageConfig?.resKey, productGroup);

    const wifiGroup = pipelineConfig.state?.[pipelineConfig?.stageConfig?.getGroup] ?? {};
    const wifiPrefix = getKeyVal(pipelineConfig?.stageConfig?.getKey, wifiGroup);

    const timestampString = new Date()
      .toISOString()
      .replace(/[-:TZ.]/g, '') // remove separators and timezone
      .slice(0, 14); // keep only YYYYMMDDHHMMSS

    const [command1, id1] = await this.write2(`product?product=${product}&wifi_prefix=${wifiPrefix}&production=${timestampString}`);
    await this.read2(command1, id1);
    await new Promise((r => setTimeout(r, 250)));
    const [command2, id2] = await this.write2(`mode?set=user`);
    await this.read2(command2, id2);
    await new Promise((r => setTimeout(r, 250)));

    return true;
  }
}
